import React, { useMemo, useCallback } from 'react';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { ROOM_COLORS } from '../utils/constants';
import { polygonCentroid, polygonArea, formatArea } from '../utils/geometry';

export function Space3D({
  space,
  points,
  isSelected = false,
  showLabel = true,
  showArea = true,
  labelSize = 60,
  fillOpacity = 0.35,
  onSelect,
  activeTool = 'select', // Aktuelles Werkzeug
}) {
  // Polygon-Koordinaten aus pointIds
  const polygon = useMemo(() => {
    return (space.pointIds || [])
      .map(pid => points.find(p => p.id === pid))
      .filter(Boolean);
  }, [space.pointIds, points]);

  // Shape für Füllung
  const shape = useMemo(() => {
    if (polygon.length < 3) return null;

    const s = new THREE.Shape();
    s.moveTo(polygon[0].x, polygon[0].y);

    for (let i = 1; i < polygon.length; i++) {
      s.lineTo(polygon[i].x, polygon[i].y);
    }

    s.closePath();
    return s;
  }, [polygon]);

  // Farbe
  const color = ROOM_COLORS[space.colorIndex % ROOM_COLORS.length];

  // Schwerpunkt für Label
  const centroid = useMemo(() => polygonCentroid(polygon), [polygon]);

  // Fläche
  const area = useMemo(() => polygonArea(polygon), [polygon]);

  const handleClick = useCallback((e) => {
    // Nur bei Select-Tool den Klick abfangen
    // Bei anderen Tools (z.B. Punkt) durchlassen
    if (activeTool === 'select') {
      e.stopPropagation();
      // Pass native event for shift-key detection
      onSelect?.(space.id, e.nativeEvent || e);
    }
    // Bei anderen Tools: Event durchlassen (kein stopPropagation)
  }, [space.id, onSelect, activeTool]);

  if (!shape) return null;

  return (
    <group>
      {/* Flächen-Füllung */}
      <mesh
        position={[0, 0, -5]}
        onClick={handleClick}
      >
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={isSelected ? Math.min(fillOpacity + 0.25, 0.8) : fillOpacity}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Umrandung bei Auswahl */}
      {isSelected && (
        <lineLoop position={[0, 0, -4]}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={polygon.length}
              array={new Float32Array(polygon.flatMap(p => [p.x, p.y, 0]))}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#3b82f6" linewidth={2} />
        </lineLoop>
      )}

      {/* Raum-Name */}
      {showLabel && (
        <Text
          position={[centroid.x, centroid.y + labelSize * 1.5, 3]}
          fontSize={labelSize}
          color="#1f2937"
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
        >
          {space.name || 'Raum'}
        </Text>
      )}

      {/* Flächen-Anzeige */}
      {showArea && area > 0 && (
        <Text
          position={[centroid.x, centroid.y - labelSize * 1.2, 3]}
          fontSize={labelSize * 0.75}
          color="#6b7280"
          anchorX="center"
          anchorY="middle"
        >
          {formatArea(area)} m²
        </Text>
      )}
    </group>
  );
}
